import { useEffect } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/trpc/trpc";

const BASE_TITLE = "Panel";

/**
 * Logo + name of the salon the panel is currently operating on. Also keeps the
 * browser tab title in sync, so the salon is identifiable from the tab strip.
 */
export function ActiveSalonBrand({ subtitle }: { subtitle: string }) {
  const { data: salon, isLoading } = trpc.salon.getOwnerSalon.useQuery();

  useEffect(() => {
    document.title = salon?.name ? `${salon.name} · ${BASE_TITLE}` : BASE_TITLE;
    return () => {
      document.title = BASE_TITLE;
    };
  }, [salon?.name]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Skeleton className="size-8 rounded-md" />
        <div className="space-y-1">
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-3 w-16" />
        </div>
      </div>
    );
  }

  const name = salon?.name ?? "Brak salonu";
  const initial = salon?.name?.trim().charAt(0).toUpperCase() || "?";

  return (
    <div className="flex items-center gap-2 min-w-0">
      <Avatar className="size-8 rounded-md">
        {salon?.logoUrl && <AvatarImage src={salon.logoUrl} alt={name} />}
        <AvatarFallback className="rounded-md bg-primary text-primary-foreground text-sm font-bold">
          {initial}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold" title={name}>
          {name}
        </p>
        <p className="truncate text-xs text-muted-foreground">
          {salon?.code ? `${salon.code} · ${subtitle}` : subtitle}
        </p>
      </div>
    </div>
  );
}
